"use client";

import React, { useCallback, useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { MODAL_IDS, useGlobalModalStack } from "@west-tennessee-consulting/ui";

import { Button } from "@/components/ui/button";
import { useCompanyContacts } from "@/features/companies/hooks/companies-base-queries";

import {
  usePrefetchSupportInboxThread,
  useSupportInbox,
  useSupportInboxThread,
  useThreadSummaries,
} from "./useSupportInbox";
import {
  resolveExternalCounterparty,
  supportInboxMessageToMessage,
} from "./supportInboxMessageToMessage";
import { SupportInboxHeader } from "./SupportInboxHeader";
import { SupportInboxNotEnrolled } from "./SupportInboxNotEnrolled";
import { SupportInboxReadingPane } from "./SupportInboxReadingPane";
import { SupportInboxSearch } from "./SupportInboxSearch";
import { SupportInboxThreadList } from "./SupportInboxThreadList";
import { buildTicketDescription } from "./support-inbox-format";
import type {
  CompanySupportInboxDisplayProps,
  ReadingMessage,
  SupportInboxCorrespondent,
  SupportInboxListMessage,
  SupportInboxThreadMessage,
} from "./types";

export function CompanySupportInboxDisplay({
  companyId,
  companyName,
}: CompanySupportInboxDisplayProps) {
  const { openModal } = useGlobalModalStack();
  const inboxQuery = useSupportInbox(companyId);
  const threads = useThreadSummaries(inboxQuery.data);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const threadQuery = useSupportInboxThread(companyId, selectedThreadId);
  const prefetchThread = usePrefetchSupportInboxThread(companyId);

  // The company's primary contact seeds create-ticket attribution.
  const { data: companyContacts } = useCompanyContacts(companyId, { limit: 1 });
  const primaryContactId = useMemo<string | undefined>(() => {
    const first = companyContacts?.[0];
    return first && typeof first.id === "string" ? first.id : undefined;
  }, [companyContacts]);

  const enrolled = inboxQuery.data?.enrolled ?? false;
  const groupEmail = inboxQuery.data?.groupEmail ?? null;

  // The real external correspondent per thread (customer to reply to), resolved
  // from Reply-To / From / To — NOT the "via <group>" From header. Drives the
  // list rows, the reading-pane banner, and the reply-target hint so the operator
  // always sees (and replies to) the actual person, never the group inbox.
  const correspondentByThread = useMemo(() => {
    const map = new Map<string, SupportInboxCorrespondent | null>();
    for (const thread of threads) {
      map.set(thread.threadId, resolveExternalCounterparty(thread.messages, groupEmail));
    }
    return map;
  }, [threads, groupEmail]);

  // Client-side filter over the loaded threads: subject, correspondent, raw from,
  // and each message snippet. Kept in-memory — the list is already bounded by the
  // API's lookback window, so no extra round-trip.
  const filteredThreads = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((thread) => {
      const correspondent = correspondentByThread.get(thread.threadId);
      const haystack = [
        thread.subject,
        thread.from,
        correspondent?.name ?? "",
        correspondent?.email ?? "",
        ...thread.messages.map((m) => m.snippet ?? ""),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [threads, query, correspondentByThread]);

  const handleEnroll = useCallback(() => {
    openModal(MODAL_IDS.companies.enrollSupportGroup, {
      companyId,
      companyName: companyName ?? undefined,
    });
  }, [openModal, companyId, companyName]);

  const handleReply = useCallback(
    (msg: SupportInboxListMessage | SupportInboxThreadMessage) => {
      // Reply to the EXTERNAL customer of the thread, never the support group or a
      // WTC staffer. A support thread interleaves the customer's inbound mail with
      // WTC's @westtn.consulting replies, so seeding the recipient from the clicked
      // message alone can wrongly target the group address.
      const thread = threads.find((t) => t.threadId === msg.threadId);
      const threadMessages = thread?.messages ?? [msg];
      const external = resolveExternalCounterparty(threadMessages, groupEmail);
      const base = supportInboxMessageToMessage(msg);
      // Set the resolved customer as `replyTo` rather than overwriting `sender`:
      // the compose modal resolves the reply recipient from `replyTo[0]` (see
      // EMAIL_REPLY_TARGETS_REPLY_TO), and leaving `sender` intact keeps the
      // quoted "From:" line truthful about who actually wrote the message.
      const message = external ? { ...base, replyTo: [external] } : base;
      openModal(MODAL_IDS.messages.compose, {
        mode: "reply",
        message,
        companyId,
        // The compose send path only reaches the customer directly — it never
        // touches the group inbox, so a reply sent from here is otherwise
        // invisible to Hub and to anyone else looking at this thread. BCC'ing
        // the group's own address redelivers the reply into it, which the
        // existing thread fetch (getGroupThread) will pick up on next refresh.
        initialBcc: groupEmail ? [groupEmail] : undefined,
      });
    },
    [openModal, companyId, threads, groupEmail],
  );

  const handleCreateTicket = useCallback(
    (msg: SupportInboxListMessage) => {
      openModal(MODAL_IDS.tickets.createQuick, {
        companyId,
        contactId: primaryContactId,
        initialSubject: msg.subject,
        initialDescription: buildTicketDescription(msg),
      });
    },
    [openModal, companyId, primaryContactId],
  );

  // Loading (first load only — don't blank a populated view on refetch).
  if (inboxQuery.isLoading && !inboxQuery.data) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading support inbox…
      </div>
    );
  }

  if (inboxQuery.isError) {
    return (
      <div className="space-y-3 p-4">
        <p className="text-sm text-destructive">
          We couldn&apos;t load the support inbox.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void inboxQuery.refetch()}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  // Not enrolled → empty state with the enroll CTA.
  if (!enrolled) {
    return (
      <SupportInboxNotEnrolled
        companyName={companyName}
        onEnroll={handleEnroll}
      />
    );
  }

  const threadListVisibleOnMobile = !selectedThreadId;

  // Reading pane: render immediately from the list data we already have (headers,
  // snippet, ticket/communication badges), then upgrade to full bodies when the
  // per-thread fetch resolves. This makes the first click feel instant instead of
  // blocking on a Gmail round-trip.
  const selectedThread = selectedThreadId
    ? threads.find((thread) => thread.threadId === selectedThreadId)
    : undefined;
  const fullThreadMessages = threadQuery.data?.messages;
  const readingMessages: ReadingMessage[] = (fullThreadMessages ??
    selectedThread?.messages ??
    []) as ReadingMessage[];
  const readingMessagesNewestFirst = [...readingMessages].sort(
    (a, b) => Date.parse(b.receivedAt) - Date.parse(a.receivedAt),
  );
  const bodiesPending = !fullThreadMessages && threadQuery.isFetching;
  const bodiesFailed = threadQuery.isError && !fullThreadMessages;

  // Who a reply in this thread will actually reach (the resolved customer).
  const selectedCorrespondent = selectedThreadId
    ? correspondentByThread.get(selectedThreadId) ?? null
    : null;

  return (
    <div className="space-y-3">
      <SupportInboxHeader
        threadCount={threads.length}
        groupEmail={groupEmail}
        isRefreshing={inboxQuery.isFetching}
        onRefresh={() => void inboxQuery.refetch()}
      />

      {/* Thread search — hidden on mobile once a thread is open to reclaim space. */}
      <SupportInboxSearch
        query={query}
        onQueryChange={setQuery}
        visibleOnMobile={threadListVisibleOnMobile}
      />

      <div className="flex flex-col md:flex-row gap-4 md:h-[70vh] md:min-h-[420px]">
        {/* Thread list (left) */}
        <SupportInboxThreadList
          totalThreadCount={threads.length}
          filteredThreads={filteredThreads}
          query={query}
          selectedThreadId={selectedThreadId}
          correspondentByThread={correspondentByThread}
          onSelectThread={setSelectedThreadId}
          onPrefetchThread={prefetchThread}
          visibleOnMobile={threadListVisibleOnMobile}
        />

        {/* Reading pane (right) */}
        <SupportInboxReadingPane
          companyId={companyId}
          selectedThreadId={selectedThreadId}
          selectedThread={selectedThread}
          correspondent={selectedCorrespondent}
          messagesNewestFirst={readingMessagesNewestFirst}
          bodiesPending={bodiesPending}
          bodiesFailed={bodiesFailed}
          onBack={() => setSelectedThreadId(null)}
          onRetryBodies={() => void threadQuery.refetch()}
          onReply={handleReply}
          onCreateTicket={handleCreateTicket}
          threadListVisibleOnMobile={threadListVisibleOnMobile}
        />
      </div>
    </div>
  );
}
